// shell/components/entity-fields/form-renderers/boolean-input.ts

import { createElement } from '../../../utils/dom';
import type { BooleanFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a toggle switch input.
 */
export function renderBooleanInput<T>(
    field: BooleanFormField<T>,
    initialValue: unknown,
    onChange: (value: boolean) => void
): FormRendererResult {
    const container = createElement('div', {
        className: 'ev-field-input ev-field-input--boolean',
    });

    const label = createElement('label', {
        className: 'ev-toggle-label',
    });

    // Label first (project convention: descriptive text precedes the control).
    if (field.checkboxLabel) {
        const labelText = createElement('span', {
            className: 'ev-toggle-text',
            textContent: field.checkboxLabel,
        });
        label.appendChild(labelText);
    }

    // Toggle wrapper
    const toggle = createElement('span', { className: 'ev-toggle' });

    const inputEl = createElement('input', {
        className: 'ev-toggle-input',
        attributes: {
            type: 'checkbox',
            ...(field.disabled ? { disabled: '' } : {}),
        },
    }) as HTMLInputElement;

    const track = createElement('span', { className: 'ev-toggle-track' });
    const knob = createElement('span', { className: 'ev-toggle-knob' });

    // Set initial value
    inputEl.checked = Boolean(initialValue);

    // Handle change
    const handleChange = () => {
        updateStateText();
        onChange(inputEl.checked);
    };
    inputEl.addEventListener('change', handleChange);

    toggle.appendChild(inputEl);
    toggle.appendChild(track);
    toggle.appendChild(knob);
    label.appendChild(toggle);

    // On/off state label
    const stateText = createElement('span', {
        className: 'ev-toggle-state',
        textContent: inputEl.checked ? 'on' : 'off',
    });
    label.appendChild(stateText);

    const updateStateText = () => {
        stateText.textContent = inputEl.checked ? 'on' : 'off';
    };

    container.appendChild(label);

    return {
        element: container,
        getValue: () => inputEl.checked,
        setValue: (value: unknown) => {
            inputEl.checked = Boolean(value);
            updateStateText();
        },
        validate: async () => true,
        focus: () => inputEl.focus(),
        setDisabled: (disabled: boolean) => {
            inputEl.disabled = disabled;
        },
        dispose: () => {
            inputEl.removeEventListener('change', handleChange);
        },
    };
}
